import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchCaeActionPlanAging,
  fetchCaeAuditorReliance,
  fetchCaeControlsByDept,
  fetchCaeControlUniverse,
  fetchCaeCoverageMatrix,
  fetchCaeExceptionsSummary,
  fetchCaeIssuesPipeline,
  fetchCaeITGCCoverage,
  fetchCaeMRC,
  fetchCaeTestingStatus,
  fetchCaeTesterWorkload,
  type CAEActionPlanAgingRow,
  type CAEAuditorRelianceRow,
  type CAEControlsByDeptRow,
  type CAEControlUniverseRow,
  type CAECoverageMatrixRow,
  type CAEExceptionsSummaryRow,
  type CAEIssuesPipelineRow,
  type CAEITGCCoverageRow,
  type CAEMRCRow,
  type CAETestingStatusRow,
  type CAETesterWorkloadRow,
} from '../../request/analytics/caeReports.ts';
import { analyticsCaeQueryKeys } from '../../queries/analytics/keys.ts';

/**
 * React Query v5 hooks for the CAE/Manager-persona analytics dashboard
 * (Feature 5.3, reports #9-#19). Each report is its own independent query
 * so a single failing/slow widget doesn't block the rest of the dashboard.
 */

export function useCaeControlUniverse(): UseQueryResult<CAEControlUniverseRow[], Error> {
  return useQuery({
    queryKey: analyticsCaeQueryKeys.controlUniverse(),
    queryFn: fetchCaeControlUniverse,
  });
}

export function useCaeTestingStatus(): UseQueryResult<CAETestingStatusRow[], Error> {
  return useQuery({
    queryKey: analyticsCaeQueryKeys.testingStatus(),
    queryFn: fetchCaeTestingStatus,
  });
}

export function useCaeIssuesPipeline(): UseQueryResult<CAEIssuesPipelineRow[], Error> {
  return useQuery({
    queryKey: analyticsCaeQueryKeys.issuesPipeline(),
    queryFn: fetchCaeIssuesPipeline,
  });
}

export function useCaeCoverageMatrix(): UseQueryResult<CAECoverageMatrixRow[], Error> {
  return useQuery({
    queryKey: analyticsCaeQueryKeys.coverageMatrix(),
    queryFn: fetchCaeCoverageMatrix,
  });
}

export function useCaeMRC(): UseQueryResult<CAEMRCRow[], Error> {
  return useQuery({
    queryKey: analyticsCaeQueryKeys.mrc(),
    queryFn: fetchCaeMRC,
  });
}

export function useCaeExceptionsSummary(): UseQueryResult<CAEExceptionsSummaryRow[], Error> {
  return useQuery({
    queryKey: analyticsCaeQueryKeys.exceptionsSummary(),
    queryFn: fetchCaeExceptionsSummary,
  });
}

export function useCaeAuditorReliance(): UseQueryResult<CAEAuditorRelianceRow[], Error> {
  return useQuery({
    queryKey: analyticsCaeQueryKeys.auditorReliance(),
    queryFn: fetchCaeAuditorReliance,
  });
}

export function useCaeActionPlanAging(): UseQueryResult<CAEActionPlanAgingRow[], Error> {
  return useQuery({
    queryKey: analyticsCaeQueryKeys.actionPlanAging(),
    queryFn: fetchCaeActionPlanAging,
  });
}

export function useCaeControlsByDept(): UseQueryResult<CAEControlsByDeptRow[], Error> {
  return useQuery({
    queryKey: analyticsCaeQueryKeys.controlsByDept(),
    queryFn: fetchCaeControlsByDept,
  });
}

export function useCaeITGCCoverage(): UseQueryResult<CAEITGCCoverageRow[], Error> {
  return useQuery({
    queryKey: analyticsCaeQueryKeys.itgcCoverage(),
    queryFn: fetchCaeITGCCoverage,
  });
}

export function useCaeTesterWorkload(): UseQueryResult<CAETesterWorkloadRow[], Error> {
  return useQuery({
    queryKey: analyticsCaeQueryKeys.testerWorkload(),
    queryFn: fetchCaeTesterWorkload,
  });
}
